<template>
    <div class="tree-item-wrapper">
        <div
            v-if="!isHidden"
            :id="`treeitem-${treeItem.type}-${treeItem.uuid}-${treeItem.id}`"
            v-tippy="{
                placement: 'top',
                interactive: false,
                content: itemName,
                onShow: () => showTooltip
            }"
            class="tree-item d-flex align-items-center px-3 py-1"
            @click.prevent="handleTreeItem()"
        >
            <c-checkbox :value="selected" />
            <div v-if="!isSelectAll" class="tree-item-id text-left">
                <span v-if="isCanvasElement && treeItem.id" class="px-2 no-wrap">ID {{ treeItem.id }}</span>
            </div>
            <div class="d-flex flex-grow-1 tree-item-text" :class="'level-' + marginLevel">
                <svg-icon
                    v-if="treeItem.icon"
                    :src="getIcon"
                    style="font-size: 16px; color: #89c3d1; fill: #89c3d1"
                    class="cursor-pointer"
                />
                <span class="ml-2 ellipsis">{{ itemName }}</span>
            </div>
            <div v-if="hasChildren && !isSelectAll" class="p-1" @click.stop="showChildren = !showChildren">
                <b-icon :icon="showChildren ? 'chevron-up' : 'chevron-down'" style="width: 16px; height: 16px" />
            </div>
        </div>
        <b-collapse v-if="hasChildren" v-model="showChildren">
            <modal-import-layers-tree-item
                v-for="(item, index) in treeItem.children"
                :key="`tree-item-${index}`"
                :tree-item="item"
                :margin-level="!isHidden ? childrenLevel : childrenLevel - 1"
            />
        </b-collapse>
        <div v-if="!hasChildren && !isCanvasElement" class="text-center py-1">No layers found</div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import store from '@/store';
import { TreeItem, TreeType } from '@/components/Layers/modal/types';
import { getCanvasElementIcon, getCanvasElementTitle } from '@/utils/elementsUtils';
import SvgIcon from '@/components/SvgIcon/SvgIcon.vue';
import CCheckbox from '@/components/shared/CCheckbox.vue';

const props = withDefaults(
    defineProps<{
        treeItem: TreeItem;
        marginLevel?: number;
    }>(),
    {
        marginLevel: 0
    }
);

const showChildren = ref(true);

const selected = computed({
    get() {
        return store.state.storeLayersModal!.selectedTreeItems.some((item) => {
            const hasUuid = [TreeType.CANVAS_ELEMENT, TreeType.PAGE_EPS, TreeType.HIDDEN].includes(item.type);
            return hasUuid ? item.uuid === props.treeItem.uuid : item.id === props.treeItem.id;
        });
    },
    set(value: boolean) {
        const methods: Record<string, (treeItem: TreeItem) => void> = {
            [TreeType.CANVAS_ELEMENT]: (treeItem) => {
                if (value) {
                    store.dispatch('storeLayersModal/addSelectedTreeItem', treeItem);
                } else {
                    store.dispatch('storeLayersModal/removeSelectedTreeItem', treeItem);
                }
            },
            default: (treeItem) => {
                methods[TreeType.CANVAS_ELEMENT](treeItem);
                treeItem.children?.forEach((item: TreeItem) => {
                    methods['default'](item);
                });
            }
        };

        (methods[props.treeItem.type] || methods['default'])(props.treeItem);
    }
});

const getIcon = computed(() => getCanvasElementIcon(undefined, props.treeItem.icon) || '');

const childrenLevel = computed(() => {
    const types: Record<string, number> = {
        '0': 1,
        '1': 2,
        '2': 3,
        ['default']: 0
    };
    return types[props.marginLevel] || types['default'];
});

const itemName = computed(() => {
    if (props.treeItem.name && props.treeItem.type !== TreeType.PAGE_EPS) {
        return props.treeItem.name;
    }

    return getCanvasElementTitle({ id: Number(props.treeItem.id) }, props.treeItem.icon);
});

const isType = (type: TreeType) => computed(() => props.treeItem.type === type);

const showTooltip = computed(() =>
    [TreeType.CANVAS_ELEMENT, TreeType.PAGE_EPS, TreeType.GROUP].includes(props.treeItem.type)
);

const isCanvasElement = isType(TreeType.CANVAS_ELEMENT);

const isSelectAll = isType(TreeType.SELECT_ALL);

const isHidden = isType(TreeType.HIDDEN);

const hasChildren = computed(() => props.treeItem.children?.length);

const handleTreeItem = () => {
    selected.value = !selected.value;
};
</script>

<style lang="scss" scoped>
.tree-item-wrapper {
    .tree-item {
        margin-left: 0;
        &:hover {
            font-weight: bold;
            background-color: #f0f1f2 !important;
            cursor: pointer;
        }
        .level-1 {
            margin-left: 0.5rem !important;
        }
        .level-2 {
            margin-left: 1rem !important;
        }
        .level-3 {
            padding-left: 1.5rem !important;
        }
        &-id {
            min-width: 60px;
        }
        &-text {
            width: 100%;
            display: flex;
            overflow: hidden;
            .ellipsis {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
}
</style>
